import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * The signed-in app's left sidebar: where it shows, how wide it is, and whether the person
 * folded it. One sidebar replaces the top bar's tool switcher, its usage chip and its account
 * menu, and the AI Agent studio's own rail (the user's call, 2026-10-10, after idea.ai's).
 *
 * `mode`: 'full' (240 px, labels), 'rail' (64 px, icons) or 'hidden'. It is hidden on the
 * marketing pages and in the video editor (a surface of its own); it opens as a rail on a
 * work page (a clip workspace, a video's page) and in full on a list or create page, unless
 * the person chose, which this browser keeps (`app.sidebar`). The width is published as
 * `--app-rail` on <html>, which `main` pads by; a phone never has it (its menu sheet is the
 * same list).
 */
const KEY = 'app.sidebar';
export const WIDTHS = { full: '240px', rail: '64px', hidden: '0px' };

const SidebarContext = createContext(null);

const APP_ROUTE = /^\/(ai-agent|ai-clipping|auto-caption|video-reframe|account|admin)(\/|$)/;
const EDITOR_ROUTE = /^\/editor(\/|$)/;
// A page that works on one thing wants the width: a clip's workspace, a video's page
const WORK_ROUTE = /^\/(ai-clipping|auto-caption|video-reframe)\/[^/]+|^\/ai-agent\/(?!series(\/|$)|characters(\/|$)|scenes(\/|$))[^/]+/;

const readChoice = () => {
  try { const v = window.localStorage.getItem(KEY); return v === 'full' || v === 'rail' ? v : null; } catch { return null; }
};

/** Where the sidebar belongs on this page, for someone signed in. */
export function sidebarModeFor(pathname, choice) {
  if (EDITOR_ROUTE.test(pathname)) return 'hidden';
  if (!APP_ROUTE.test(pathname) && pathname !== '/') return 'hidden';
  if (choice) return choice;
  return WORK_ROUTE.test(pathname) ? 'rail' : 'full';
}

export function SidebarProvider({ children }) {
  const { pathname } = useLocation();
  const auth = useAuth();
  const isAuthenticated = Boolean(auth?.isAuthenticated);
  const [choice, setChoice] = useState(readChoice);
  const mode = isAuthenticated ? sidebarModeFor(pathname, choice) : 'hidden';

  useEffect(() => {
    document.documentElement.style.setProperty('--app-rail', WIDTHS[mode]);
    return () => document.documentElement.style.removeProperty('--app-rail');
  }, [mode]);

  const toggle = useCallback(() => {
    const next = mode === 'full' ? 'rail' : 'full';
    try { window.localStorage.setItem(KEY, next); } catch { /* kept for this visit only */ }
    setChoice(next);
  }, [mode]);

  const value = useMemo(() => ({ mode, shown: mode !== 'hidden', toggle, hostsStudioRail: mode !== 'hidden' }), [mode, toggle]);
  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

/** Outside the provider (a test, the harness without it): no sidebar. */
export const useSidebar = () => useContext(SidebarContext) || { mode: 'hidden', shown: false, toggle: () => {}, hostsStudioRail: false };
